import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { brandSettingsProgressionRanksPath } from "@/routes"
import { type LadderRank, RANK_NAME_MAX_LENGTH } from "@/types/progression"

import { PrivilegesEditor } from "./privileges-editor"
import { RankBlazonUploader } from "./rank-blazon-uploader"

/**
 * OKS-52 — Card A on the Progression page: full rank ladder editor.
 *
 * Hosts ONE Inertia `<Form method="patch">`. Each rank is a
 * `<fieldset>` with name + min_xp + nested `<PrivilegesEditor>`.
 * Bottom: single Save button.
 *
 * Wire shape per `BrandSettingsRanksController` strong-params
 * allow-list:
 *
 *   ladder[ranks][i][id]
 *   ladder[ranks][i][name]
 *   ladder[ranks][i][min_xp]
 *   ladder[ranks][i][privileges][j][...]   (see PrivilegesEditor)
 *
 * Lowest tier's `min_xp` input is `disabled` (TRD KD-4 — visible but
 * not editable; defence-in-depth on top of the server's "lowest must
 * be 0" cross-row check). Server-side cross-row violations land at
 * the top-level `ladder` error key and surface in a destructive
 * `<Alert>` above the Save button.
 *
 * OKS-294 — Each rank's `<fieldset>` is delegated to `<RankRow>`,
 * which owns one `useState<string>` for the in-flight rank name.
 * The `<legend>` and the nested `<PrivilegesEditor>`'s legend
 * ("Privileges for {name}") both read this live value so they
 * track each keystroke. The name `<Input>` is therefore controlled
 * (`value` + `onChange`); the min-XP input stays uncontrolled
 * (TRD §2 KD-2). Submit payload is unchanged — the controlled
 * input still ships `ladder[ranks][i][name]` carrying the live
 * value via Inertia `<Form>`.
 */
export function RankLadderForm({
  ladder,
  canEdit,
}: {
  ladder: LadderRank[]
  canEdit: boolean
}) {
  return (
    <Card data-testid="brand-settings-progression-ranks-card">
      <CardHeader>
        <RanksHeading />
      </CardHeader>
      <CardContent>
        <Form
          method="patch"
          action={brandSettingsProgressionRanksPath()}
          options={{ preserveScroll: true }}
          className="space-y-6"
          data-testid="brand-settings-progression-ranks-form"
        >
          {({ errors, processing }) => {
            // Inertia errors come back as `Record<string, string>` keyed
            // by the field path (e.g. `ranks[0][min_xp]`, `ladder`).
            const errs = errors as Record<string, string | string[]>
            const ladderError =
              typeof errs.ladder === "string"
                ? errs.ladder
                : Array.isArray(errs.ladder)
                  ? errs.ladder.join(", ")
                  : undefined

            return (
              <>
                {ladderError ? (
                  <LadderErrorAlert message={ladderError} />
                ) : null}

                {ladder.map((rank, idx) => (
                  <RankRow
                    key={rank.id}
                    rank={rank}
                    idx={idx}
                    tierCount={ladder.length}
                    errors={errs}
                    canEdit={canEdit}
                  />
                ))}

                <SaveOrReadonly canEdit={canEdit} processing={processing} />
              </>
            )
          }}
        </Form>
      </CardContent>
    </Card>
  )
}

function RanksHeading() {
  const { t } = useTranslation()
  return (
    <HeadingSmall
      title={t("settings.brand.progression.ranks.title")}
      description={t("settings.brand.progression.ranks.description")}
    />
  )
}

function LadderErrorAlert({ message }: { message: string }) {
  return (
    <Alert
      variant="destructive"
      data-testid="brand-settings-progression-ranks-ladder-error"
    >
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  )
}

function SaveOrReadonly({
  canEdit,
  processing,
}: {
  canEdit: boolean
  processing: boolean
}) {
  const { t } = useTranslation()
  if (!canEdit) {
    return (
      <p
        role="status"
        data-testid="brand-settings-progression-ranks-readonly"
        className="text-muted-foreground text-sm"
      >
        {t("settings.brand.progression.read_only_hint")}
      </p>
    )
  }
  return (
    <Button
      type="submit"
      disabled={processing}
      data-testid="brand-settings-progression-ranks-save"
      className="min-h-11 w-full sm:w-auto"
    >
      {processing
        ? t("settings.brand.progression.ranks.saving")
        : t("settings.brand.progression.ranks.save")}
    </Button>
  )
}

/**
 * One `<fieldset>` per rank — owns the in-flight rank name in local
 * state so the `<legend>` (and the nested privileges editor's
 * legend) tracks the input keystroke-by-keystroke.
 *
 * Mirrors the lazy-initializer pattern used by `<PrivilegesEditor>`
 * (one component owns one rank's working state). State is seeded
 * ONCE from the `rank.name` prop on mount; no `useEffect`
 * prop-sync — Inertia successful-save reload remounts the form,
 * so the next render naturally re-seeds from the persisted prop.
 */
function RankRow({
  rank,
  idx,
  tierCount,
  errors,
  canEdit,
}: {
  rank: LadderRank
  idx: number
  tierCount: number
  errors: Record<string, string | string[]>
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const [liveName, setLiveName] = useState<string>(() => rank.name)

  const isLowest = idx === 0
  const nameErr = errors[`ranks[${idx}][name]`]
  const minXpErr = errors[`ranks[${idx}][min_xp]`]
  const nameMessages =
    nameErr == null ? undefined : Array.isArray(nameErr) ? nameErr : [nameErr]
  const minXpMessages =
    minXpErr == null
      ? undefined
      : Array.isArray(minXpErr)
        ? minXpErr
        : [minXpErr]

  return (
    <fieldset
      data-testid={`brand-settings-progression-rank-${idx}`}
      className="space-y-4 rounded-md border p-4"
    >
      <legend
        className="text-base font-medium"
        data-testid={`rank-${idx}-legend`}
      >
        {liveName}
      </legend>

      <input type="hidden" name={`ladder[ranks][${idx}][id]`} value={rank.id} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor={`rank-${idx}-name`}>
            {t("settings.brand.progression.ranks.name_label")}
          </Label>
          <Input
            id={`rank-${idx}-name`}
            type="text"
            name={`ladder[ranks][${idx}][name]`}
            value={liveName}
            onChange={(e) => setLiveName(e.target.value)}
            maxLength={RANK_NAME_MAX_LENGTH}
            readOnly={!canEdit}
            aria-readonly={!canEdit}
            aria-invalid={nameMessages ? true : undefined}
            data-testid={`brand-settings-progression-rank-${idx}-name`}
          />
          <InputError messages={nameMessages} />
        </div>

        <div className="grid gap-2">
          <Label htmlFor={`rank-${idx}-min-xp`}>
            {t("settings.brand.progression.ranks.min_xp_label")}
          </Label>
          <Input
            id={`rank-${idx}-min-xp`}
            type="number"
            name={`ladder[ranks][${idx}][min_xp]`}
            defaultValue={rank.min_xp}
            step={1}
            inputMode="numeric"
            readOnly={!canEdit}
            aria-readonly={!canEdit}
            disabled={isLowest}
            aria-disabled={isLowest}
            aria-describedby={
              isLowest ? `rank-${idx}-min-xp-helper` : undefined
            }
            aria-invalid={minXpMessages ? true : undefined}
            data-testid={`brand-settings-progression-rank-${idx}-min-xp`}
          />
          {/* Lowest-tier input is disabled, so the browser won't
              submit its value. Mirror `0` via a hidden input so the
              strong-params receive a complete payload. */}
          {isLowest ? (
            <>
              <input
                type="hidden"
                name={`ladder[ranks][${idx}][min_xp]`}
                value={0}
              />
              <p
                id={`rank-${idx}-min-xp-helper`}
                className="text-muted-foreground text-xs"
              >
                {t("settings.brand.progression.ranks.lowest_locked_helper")}
              </p>
            </>
          ) : null}
          <InputError messages={minXpMessages} />
        </div>
      </div>

      <PrivilegesEditor
        rankIdx={idx}
        rankName={liveName}
        initialPrivileges={rank.privileges}
        errors={errors}
        canEdit={canEdit}
      />

      {/* OKS-539 — Per-rank blazon uploader. Submits to its OWN dedicated
          endpoint via `router` (NOT nested in the ladder <Form>), decoupled
          from the atomic ladder save (PRD Q4). `tier` = idx + 1 (ladder is
          ordered by min_xp; index+1 == position). */}
      <RankBlazonUploader
        rankId={rank.id}
        rankName={liveName}
        tier={idx + 1}
        tierCount={tierCount}
        imageUrl={rank.image_url}
        readOnly={!canEdit}
      />
    </fieldset>
  )
}
